import { contentClient } from '@wajeez-web/core/api-base';
import { thunkErrorHandler } from '@wajeez-web/core/utils';
import { ApiMethod } from '@wajeez-web/stores/base';
import { AlertType, CommonActions } from '@wajeez-web/stores/common';
import { Server } from '@wajeez/wajeez.microservices';

export const getComponentTypesDatatable: ApiMethod<
  Server.ComponentTypeDatatable,
  Server.TableOfComponentTypeColumns,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.componentTypes_datatable_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentTypesLookups: ApiMethod<
  void | { usageType?: Server.ComponentUsageType; searchKey?: string },
  Server.ComponentTypeOutput[],
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.componentTypes_lookups_get_V1(
      undefined,
      data?.searchKey,
      data?.usageType
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentTypesByCollectionComponentId: ApiMethod<
  {
    searchKey?: string | null | undefined;
    collectionComponentId: string | undefined;
  },
  Server.ComponentTypeOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const { searchKey, collectionComponentId } = data;

    const response =
      await client.componentTypes_lookups_allowedComponentTypesByCollectionComponentId_get_V1(
        undefined,
        searchKey,
        collectionComponentId
      );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentTypesByCollectionComponentTypeId: ApiMethod<
  {
    searchKey?: string | null | undefined;
    collectionComponentTypeId: number | undefined;
  },
  Server.ComponentTypeOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const { searchKey, collectionComponentTypeId } = data;

    const response =
      await client.componentTypes_lookups_allowedComponentTypesByCollectionComponentTypeId_get_V1(
        undefined,
        searchKey,
        collectionComponentTypeId
      );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentTypeById: ApiMethod<
  number | undefined,
  Server.ComponentTypeOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.componentTypes_byId_get_V1(undefined, id);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentTypeByIdMultilingual: ApiMethod<
  number,
  Server.ComponentTypeMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ComponentTypes_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_ComponentTypes_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const createComponentType: ApiMethod<
  Server.CreateComponentTypeInput,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.componentTypes_post_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createComponentTypeMultilingual: ApiMethod<
  Server.CreateComponentTypeV2Input,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ComponentTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ComponentTypes_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editComponentTypeMultilingual: ApiMethod<
  Server.UpdateComponentTypeInput,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ComponentTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ComponentTypes_put_V1(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};
